import keyBy from 'lodash/keyBy'
import moment from 'moment-timezone'
import { ASSIGNED_SHIPMENT_STATUS } from '../apps/booking/constants/shipments'
import { getUserCookieValues } from '../utils/userAuth'

const { userPermissions, userType } = getUserCookieValues()

export type TStatus =
  | 'e_submitted'
  | 'submitted'
  | 'quoted'
  | 'on_hold'
  | 'tendered'
  | 'alerted'
  | 'waterfall_offered'
  | 'offered'
  | 'covered'
  | 'assigned'
  | 'dispatched'
  | 'arrived_for_pickup'
  | 'in_transit'
  | 'arrived_for_delivery'
  | 'delivered'
  | 'documents_received'
  | 'pod_rejected'
  | 'invoice_sent'
  | 'truck_ordered_not_used'
  | 'completed'
  | 'canceled'

export type TOption<T extends string> = { label: string; value: T }

// Statuses for shipment list filters
export const SHIPMENT_STATUSES: TOption<TStatus>[] = [
  { value: 'e_submitted', label: 'E-Submitted' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'tendered', label: 'Tendered' },
  { value: 'alerted', label: 'Alerted' },
  { value: 'waterfall_offered', label: 'Reserved' },
  { value: 'offered', label: 'Offered' },
  { value: 'covered', label: 'Covered' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'arrived_for_pickup', label: 'At Pickup' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'arrived_for_delivery', label: 'At Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'documents_received', label: 'Documents Received' },
  { value: 'pod_rejected', label: 'POD Rejected' },
  { value: 'invoice_sent', label: 'Invoice Sent' },
  { value: 'truck_ordered_not_used', label: 'TONU' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
]

export const OFFERED_STATUSES = ['offered', 'reserved', 'waterfall_offered']

export const UPCOMING_STATUSES = [
  'submitted',
  'tendered',
  'no_appointment_time',
  'on_hold',
  'offered',
  'waterfall_offered',
  'reserved',
  'covered',
  'assigned',
]

export const PRE_PICKUP_STATUSES = ['dispatched', 'arrived_for_pickup']

export const PICKED_UP_EVENTS = ['in_transit', 'arrived_for_delivery']

export const DRIVING_STATUSES = [...PRE_PICKUP_STATUSES, ...PICKED_UP_EVENTS]

export const MOBILE_TRACKING_STATUSES = [
  ...UPCOMING_STATUSES,
  ...PRE_PICKUP_STATUSES,
  ...PICKED_UP_EVENTS,
]

export const ARRIVED_STATUSES = ['arrived_for_pickup', 'arrived_for_delivery']

export const DELIVERED_EVENTS = [
  'delivered',
  'documents_received',
  'pod_rejected',
  'invoice_sent',
  'completed',
]

export const UPLOAD_POD_STATUSES = ['delivered', 'pod_rejected']

export const HISTORICAL_STATUSES = [
  'arrived_for_delivery',
  'delivered',
  'documents_received',
  'pod_rejected',
  'invoice_sent',
  'completed',
]

export const MAPBOX_ETA_STATUSES = [
  ...ARRIVED_STATUSES,
  ...ASSIGNED_SHIPMENT_STATUS,
  ...PRE_PICKUP_STATUSES,
]

export const ALL_TRACKING_STATUSES = [
  ...UPCOMING_STATUSES,
  ...PRE_PICKUP_STATUSES,
  ...HISTORICAL_STATUSES,
].filter(status => {
  const notActiveStatuses = [...PICKED_UP_EVENTS, ...DELIVERED_EVENTS]
  return !notActiveStatuses.includes(status)
})

// Statuses for the nextStatus
export const KEYED_SHIPMENT_STATUSES = keyBy(SHIPMENT_STATUSES, ({ value }) => value) as Record<
  TStatus,
  typeof SHIPMENT_STATUSES[number]
>

export const PRE_PICKUP_LABELS = [
  KEYED_SHIPMENT_STATUSES.dispatched.label,
  KEYED_SHIPMENT_STATUSES.arrived_for_pickup.label,
]

export const PICKUP_LABELS = [
  KEYED_SHIPMENT_STATUSES.arrived_for_pickup.label,
  KEYED_SHIPMENT_STATUSES.arrived_for_delivery.label,
]

export const BEFORE_TRANSIT_LABELS = [
  KEYED_SHIPMENT_STATUSES.dispatched.label,
  KEYED_SHIPMENT_STATUSES.in_transit.label,
]

export const LEAVING_LABELS = [
  KEYED_SHIPMENT_STATUSES.in_transit.label,
  KEYED_SHIPMENT_STATUSES.delivered.label,
]

// Statuses for the chart
export const ACTIVE_STATUSES = [
  'Tendered',
  'In Auction',
  'No Appointment Time',
  'On Hold',
  'Offered',
  'Covered',
  'Assigned',
  'Dispatched',
  'At Pickup',
  'In Transit',
  'At Delivery',
  'Delivered',
  'Documents Received',
  'POD Rejected',
  'Invoice Sent',
  'Completed',
]
export const CHART_CATEGORIES = {
  Tendered: [
    'Tendered',
    'In Auction',
    'No Appointment Time',
    'On Hold',
    'Offered',
    'Covered',
    'Assigned',
    'Dispatched',
  ],
  Pickup: ['At Pickup'],
  'In Transit': ['In Transit'],
  Delivery: ['At Delivery'],
  Delivered: ['Delivered', 'Documents Received', 'POD Rejected', 'Invoice Sent', 'Completed'],
}

export const DELIVERED_STATUSES = [...CHART_CATEGORIES.Delivery, ...CHART_CATEGORIES.Delivered]

export const POST_DELIVERED_STATUSES = [
  'Documents Received',
  'POD Rejected',
  'Invoice Sent',
  'Completed',
]

/**
 * @example getStatusLabelsInRange('e_submitted', 'submitted') // ['e_submitted', 'submitted']
 */
export const getStatusLabelsInRange = (fromStatus: string, toStatus: string): string[] => {
  let inRange = false
  return SHIPMENT_STATUSES.reduce<string[]>((statusLabels, status) => {
    if (!inRange && status.value === fromStatus) inRange = true
    const statuses = inRange ? [...statusLabels, status.label] : statusLabels
    if (inRange && status.value === toStatus) inRange = false
    return statuses
  }, [])
}

/**
 * @example getStatusLabelsInRange('e_submitted', 'submitted') // ['E-Submitted', 'Submitted']
 */
export const getStatusKeysInRange = (fromStatus: string, toStatus: string): TStatus[] => {
  let inRange = false
  return SHIPMENT_STATUSES.reduce<TStatus[]>((statusLabels, status) => {
    if (!inRange && status.value === fromStatus) inRange = true
    const statuses = inRange ? [...statusLabels, status.value] : statusLabels
    if (inRange && status.value === toStatus) inRange = false
    return statuses
  }, [])
}

/**
 * @example getStatusKeysUpTo('quoted') // ['e_submitted', 'submitted', 'quoted']
 */
export const getStatusKeysUpTo = (status: TStatus): string[] => {
  return getStatusKeysInRange('e_submitted', status)
}

export const getStatusLabelsUpTo = (statusValue: TStatus): string[] => {
  return getStatusLabelsInRange('e_submitted', statusValue)
}

export const getStatusLabelsFrom = (statusValue: TStatus): string[] => {
  return getStatusLabelsInRange(statusValue, 'canceled')
}

export const PRE_TRANSIT_STATUSES = getStatusLabelsUpTo('covered')
export const PRE_TRACKING_STATUSES = getStatusLabelsUpTo('assigned')
export const IN_TRANSIT_STATUSES = getStatusLabelsInRange('assigned', 'arrived_for_delivery')

export const IN_TRANSIT_TO_NEXT_STOP_STATUSES = SHIPMENT_STATUSES.filter(status => {
  const trackingStatuses = ['assigned', 'dispatched', 'in_transit'].includes(status.value)
  return trackingStatuses
}).map(status => status.label)

export const TRACKING_STATUSES = getStatusLabelsInRange('assigned', 'invoice_sent')
export const POST_TRANSIT_STATUSES = getStatusLabelsFrom('delivered')

export const DIALOG_UPDATE_STATUSES = getStatusLabelsInRange(
  KEYED_SHIPMENT_STATUSES.assigned.value,
  KEYED_SHIPMENT_STATUSES.delivered.value
)

export const FORCE_COMPLETE_SHIPMENT_REASONS = [
  {
    label: 'Did not receive documents',
    value: 'did_not_receive_documents',
  },
  {
    label: 'Partial payment received',
    value: 'partial_payment_received',
  },
  {
    label: 'Others',
    value: 'others',
  },
]

export type TShipmentTab = {
  key: string
  name: string
  statusList: string[]
  ignoreLiteralStatus?: boolean
}

export const SHIPMENT_TABS: TShipmentTab[] = [
  { key: 'submitted', name: 'Submitted', statusList: ['e_submitted', 'submitted', 'quoted'] },
  { key: 'on-hold', name: 'On-Hold', statusList: ['on_hold'] },
  { key: 'offers', name: 'Offers', statusList: ['offered'] },
  { key: 'ready-to-book', name: 'Ready-to-Book', statusList: ['tendered', 'offered', 'covered'] },
  {
    key: 'shipper-pre-transit',
    name: 'Pre-Transit',
    statusList: ['tendered', 'offered', 'covered'],
  },
  {
    key: 'carrier-pre-transit',
    name: 'Pre-Transit',
    statusList: ['submitted', 'quoted', 'tendered', 'on_hold', 'covered'],
  },
  {
    key: 'transit',
    name: 'Transit',
    statusList: [
      'assigned',
      'dispatched',
      'arrived_for_pickup',
      'in_transit',
      'arrived_for_delivery',
    ],
  },
  {
    key: 'rolled-bounced',
    name: 'Rolled/Bounced',
    statusList: ['rolled_bounced'],
    ignoreLiteralStatus: true,
  },
  { key: 'at-risk', name: 'At-Risk', statusList: ['at_risk'], ignoreLiteralStatus: true },
  {
    key: 'post-transit',
    name: 'Post-Transit',
    statusList: [
      'truck_ordered_not_used',
      'documents_received',
      'pod_rejected',
      'invoice_sent',
      'delivered',
    ],
  },
  { key: 'archived', name: 'Archived', statusList: ['completed', 'canceled'] },
  {
    key: 'shipper-archived',
    name: 'Archived',
    statusList: [
      'completed',
      'canceled',
      'invoice_sent',
      'truck_ordered_not_used',
      'documents_received',
      'pod_rejected',
      'delivered',
    ],
  },
  {
    key: 'historical',
    name: 'Historical',
    statusList: ['delivered', 'documents_received', 'invoice_sent', 'completed'],
  },
]

const selectTab = (value: string): TShipmentTab | undefined => {
  return SHIPMENT_TABS.find(tab => tab.key === value)
}

export const GET_USER_TABS = (type: string, permissions: any) => {
  const tabs = []
  if (permissions.root) {
    tabs.push(selectTab('submitted'))
    tabs.push(selectTab('on-hold'))
    tabs.push(selectTab('ready-to-book'))
    tabs.push(selectTab('rolled-bounced'))
  } else if (type === 'carrier') {
    tabs.push(selectTab('offers'))
    tabs.push(selectTab('carrier-pre-transit'))
  } else if (type === 'shipper') {
    tabs.push(selectTab('on-hold'))
    tabs.push(selectTab('shipper-pre-transit'))
  }

  // Everyone can see in-transit shipments
  tabs.push(selectTab('transit'))

  if (permissions.root) tabs.push(selectTab('at-risk'))
  if (permissions.shipments_post_transit_tab) {
    tabs.push(selectTab('post-transit'))
    tabs.push(selectTab('archived'))
  } else if (type === 'shipper') {
    tabs.push(selectTab('shipper-archived'))
  }
  return tabs
}

export const USER_TABS = GET_USER_TABS(userType, userPermissions || {})

export const NUM_HOURS_TO_NOTIFY = 12
export const PER_PAGE_COUNT = 2000
export const PAST_TIME_RANGE = moment().subtract(NUM_HOURS_TO_NOTIFY, 'hours').utc().format()
export const FUTURE_TIME_RANGE = moment().add(NUM_HOURS_TO_NOTIFY, 'hours').utc().format()

// Declared value is stored in cents, so this value represents 100000.00
export const HIGH_DECLARED_VALUE_AMOUNT = 10000000
